import { supabase } from './supabase';

type TranscriptMessage = {
  role: 'customer' | 'assistant';
  text: string;
};

const SESSION_KEY = '247ai360_chat_capture_session';
let memorySessionId = '';

function newSessionId(): string {
  const randomPart = globalThis.crypto?.randomUUID?.() || Math.random().toString(36).slice(2);
  const id = `web_${Date.now()}_${randomPart}`;
  memorySessionId = id;
  try {
    sessionStorage.setItem(SESSION_KEY, id);
  } catch {
    // Some privacy modes disable sessionStorage; the in-memory id is enough.
  }
  return id;
}

function getSessionId(): string {
  try {
    return sessionStorage.getItem(SESSION_KEY) || memorySessionId || newSessionId();
  } catch {
    return memorySessionId || newSessionId();
  }
}

function readMessages(root: Element): TranscriptMessage[] {
  const area = root.querySelector('.vapi-conversation-area');
  if (!area) return [];

  return Array.from(area.children).flatMap((row) => {
    const text = row.querySelector('.markdown-content')?.textContent?.trim();
    if (!text) return [];

    return [{
      role: row.classList.contains('justify-end') ? 'customer' : 'assistant',
      text,
    } as TranscriptMessage];
  });
}

function formatTranscript(messages: TranscriptMessage[]): string {
  return messages
    .map(({ role, text }) => `${role === 'customer' ? 'Customer' : 'Sarah'}: ${text}`)
    .join('\n\n');
}

const SPOKEN_DIGITS: Record<string, string> = {
  zero: '0', oh: '0', one: '1', two: '2', three: '3', four: '4',
  five: '5', six: '6', sex: '6', seven: '7', eight: '8', nine: '9',
};

function extractSpokenPhone(text: string): string | null {
  const token = '(?:zero|oh|one|two|three|four|five|six|sex|seven|eight|nine|\\d)';
  const matches = text.match(new RegExp(`${token}(?:[\\s,.-]+${token}){6,14}`, 'gi')) || [];
  const candidates = matches.map((match) =>
    (match.match(/zero|oh|one|two|three|four|five|six|sex|seven|eight|nine|\d/gi) || [])
      .map((part) => SPOKEN_DIGITS[part.toLowerCase()] ?? part)
      .join('')
  ).filter((digits) => digits.length >= 7 && digits.length <= 15);

  // An email username can also contain spaced digits. The actual phone number
  // is normally the longest digit sequence in the conversation.
  return candidates.sort((a, b) => b.length - a.length)[0] || null;
}

function extractSpokenEmail(text: string): string | null {
  const direct = text.match(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/i)?.[0];
  if (direct) return direct.toLowerCase();

  // Prefer a username the caller explicitly spells: "v i r e l i t h 33 at
  // gmail dot com". This is more reliable than an earlier phonetic guess.
  const spelled = text.match(/((?:\b[a-z]\s+){3,}\b[a-z])\s*(\d+)\s+at\s+g\s*mail\s+dot\s+com/i);
  if (spelled) return `${spelled[1].replace(/\s+/g, '')}${spelled[2]}@gmail.com`.toLowerCase();

  const spoken = text.match(/(?:email(?:\s+would be|\s+is)?\s+)?([a-z][a-z0-9._-]*(?:\s+[a-z0-9._-]+){0,20})\s+at\s+g\s*mail\s+dot\s+com/i);
  return spoken ? `${spoken[1].replace(/\s+/g, '')}@gmail.com`.toLowerCase() : null;
}

function extractContactDetails(messages: TranscriptMessage[]) {
  const customerText = messages
    .filter(({ role }) => role === 'customer')
    .map(({ text }) => text)
    .join('\n');
  const email = extractSpokenEmail(customerText);
  const numericPhone = (customerText.match(/(?:\+?\d[\d().\s-]{5,}\d)/g) || []).find((value) => {
    const digits = value.replace(/\D/g, '');
    return digits.length >= 7 && digits.length <= 15;
  })?.replace(/\D/g, '') || null;
  const phone = extractSpokenPhone(customerText) || numericPhone;
  // Prefer an explicit introduction. A loose "I am" match can otherwise turn
  // small talk such as "I am good" into the caller's name.
  const namePatterns = [
    /my name is[\s,]*(?:(?:uh|um|er|ah)[\s,]*)*([a-z][a-z'-]*(?:\s+[a-z][a-z'-]*)?)/gi,
    /this is[\s,]*(?:(?:uh|um|er|ah)[\s,]*)*([a-z][a-z'-]*(?:\s+[a-z][a-z'-]*)?)/gi,
    /i(?:'m| am)[\s,]*(?:(?:uh|um|er|ah)[\s,]*)*([a-z][a-z'-]*(?:\s+[a-z][a-z'-]*)?)/gi,
  ];
  let name: string | null = null;
  for (const pattern of namePatterns) {
    const matches = Array.from(customerText.matchAll(pattern));
    if (matches.length) {
      name = matches.at(-1)?.[1]?.trim() || null;
      break;
    }
  }
  const spelledNameParts = Array.from(
    customerText.matchAll(/(?:spell(?:ed)?\s+)?((?:\b[a-z]\s+){3,}\b[a-z])/gi)
  );
  const spelledLastName = spelledNameParts.at(-1)?.[1]?.replace(/\s+/g, '') || null;
  if (name && spelledLastName && !name.toLowerCase().includes(spelledLastName.toLowerCase())) {
    name = `${name.split(/\s+/)[0]} ${spelledLastName[0].toUpperCase()}${spelledLastName.slice(1).toLowerCase()}`;
  }
  const summary = /contact me|call me|reach me|somebody from your team/i.test(customerText)
    ? 'Customer requested a follow-up from the team'
    : customerText.split('\n').find((line) => line.trim())?.slice(0, 500) || 'Chat inquiry';

  return { email, phone, name, summary };
}

export function captureWebsiteConversation(messages: TranscriptMessage[], source = 'website_transcript_bridge'): void {
  if (!messages.some(({ role }) => role === 'customer')) return;

  const contact = extractContactDetails(messages);
  void supabase.rpc('capture_website_conversation', {
    payload: {
      eventType: 'conversation_snapshot',
      conversationId: getSessionId(),
      messages,
      transcript: formatTranscript(messages),
      userName: contact.name,
      userEmail: contact.email,
      userPhone: contact.phone,
      summary: contact.summary,
      source,
      status: 'new',
    },
  }).then(({ error }) => {
    if (error) {
      // A later transcript mutation retries automatically.
      console.error('Conversation capture failed', error.message);
    }
  });
}

export function startReaddyTranscriptBridge(): void {
  let observedRoot: Element | null = null;
  let transcriptObserver: MutationObserver | null = null;
  let saveTimer: number | undefined;
  let lastSavedTranscript = '';

  const saveIfChanged = (root: Element) => {
    const messages = readMessages(root);
    const transcript = formatTranscript(messages);
    if (!transcript || transcript === lastSavedTranscript) return;
    lastSavedTranscript = transcript;
    captureWebsiteConversation(messages);
  };

  const attach = () => {
    const root = document.querySelector('.vapi-widget-wrapper');
    if (!root || root === observedRoot) return;

    observedRoot = root;
    transcriptObserver?.disconnect();

    transcriptObserver = new MutationObserver(() => {
      window.clearTimeout(saveTimer);
      saveTimer = window.setTimeout(() => saveIfChanged(root), 1200);
    });
    transcriptObserver.observe(root, { childList: true, subtree: true, characterData: true });

    root.querySelector('[title="Reset conversation"]')?.addEventListener('click', () => {
      saveIfChanged(root);
      newSessionId();
      lastSavedTranscript = '';
    });

    root.querySelector('#vapi-chat-button')?.addEventListener('click', () => {
      window.setTimeout(() => saveIfChanged(root), 1800);
      window.setTimeout(() => saveIfChanged(root), 6000);
    });
    root.querySelector('#vapi-voice-button')?.addEventListener('click', () => {
      window.setTimeout(() => saveIfChanged(root), 2000);
    });
  };

  const pageObserver = new MutationObserver(attach);
  pageObserver.observe(document.documentElement, { childList: true, subtree: true });
  attach();

  // Readdy can replace its React subtree without preserving our event hooks.
  // Polling only serializes when text changed, so it is cheap and guarantees
  // voice transcripts are captured even if no stable "call ended" event exists.
  window.setInterval(() => {
    attach();
    if (observedRoot) saveIfChanged(observedRoot);
  }, 2500);
}
